import { useLazyQuery } from "@apollo/client/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ExportBatchEvidencesJobDryRunResultDocument,
  type ExportBatchEvidencesJobDryRunResultQuery,
  type ExportBatchEvidencesJobDryRunResultQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";
import { downloadFile } from "~/utils/download";

type UseExportBatchEvidencesJobDryRunResultProps = {
  jobRunId: string;
  onClose?: () => void;
};

export const useExportBatchEvidencesJobDryRunResult = ({
  jobRunId,
  onClose,
}: UseExportBatchEvidencesJobDryRunResultProps) => {
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [exportDryRunResult] = useLazyQuery<
    ExportBatchEvidencesJobDryRunResultQuery,
    ExportBatchEvidencesJobDryRunResultQueryVariables
  >(ExportBatchEvidencesJobDryRunResultDocument, {
    fetchPolicy: "no-cache",
  });
  const { tenantId } = useTenant();
  const onExport = () => {
    if (!tenantId) {
      toast.error(translateError(t, new Error("Tenant is not resolved")));
      return;
    }

    setExporting(true);

    exportDryRunResult({
      variables: { input: { tenantId, jobRunId } },
    })
      .then(({ data }) => {
        const url = data?.cnutExportBatchEvidencesJobDryRunResult;
        if (!url) {
          toast.error(t("feature.common.failedToExport"));
          return;
        }

        downloadFile(url, `dewey-batch-evidences-job-dry-run-result-${jobRunId}.csv`);
      })
      .catch((e: unknown) => {
        if (e instanceof Error) {
          toast.error(translateError(t, e));
        }
      })
      .finally(() => {
        setExporting(false);
        onClose?.();
      });
  };

  return {
    onExport,
    exporting,
  };
};
